/**
 * Excel Import Wizard schemalari
 *
 * Eski loyiha (AvtoFix-main) wizard'i asosida:
 *   - Step 1: Upload fayl
 *   - Step 2: Preview — sarlavha, sample rows, auto-detected column mapping
 *   - Step 3: Settings — kategoriya, default stock/markup/currency
 *   - Step 4: Import
 *   - Step 5: Result — total/created/updated/errors/duplicates
 *
 * Preview endpoint'i faylni oladi, headers va first N qatorlarni o'qib,
 * canonical column mapping'ni taxmin qiladi. Hech qanday DB yozuv bermaydi.
 */
import { z } from 'zod';
import { uuidSchema } from './common.schema.js';
// Kanonik kalitlar (Excel qatorlari mapping qilinadigan)
//
// Faza H+3 yangilanish — avto-qism biznesi uchun majburiy 3 ta yangi maydon:
//   - code           : ishlab chiqaruvchi/ichki kod (Код, 34485)
//   - catalogNumber  : OEM/parts katalog № (№ по каталогу, AZ16424430079)
//   - categoryName   : Excel'dan to'g'ridan-to'g'ri kategoriya nomi
//
// Bu maydonlar mexanik uchun qidirish-da kalit (3 yo'l: code / catalogNumber / name).
// `barcode` skanerlanadigan oddiy shtrix-kod (alohida tushuncha).
export const EXCEL_CANONICAL_KEYS = [
    'name',
    'sku',
    'code',
    'catalogNumber',
    'barcode',
    'categoryName',
    'description',
    'basePrice',
    'priceMultiplier',
    'currency',
    'stock',
    'minStock',
    'unit',
    'status',
];
export const excelCanonicalKeyEnum = z.enum(EXCEL_CANONICAL_KEYS);
/**
 * Column mapping — user tomonidan tahrirlanishi mumkin.
 *
 * Key: column index (1-based, Excel kolonna raqami)
 * Value: canonical key ('name' | 'sku' | ...) yoki undefined (ignore)
 *
 * Example:
 *   { 1: 'sku', 2: 'name', 3: 'basePrice', 4: 'stock' }
 */
export const columnMappingSchema = z.record(z.string().regex(/^\d+$/, 'Column index must be a positive integer'), excelCanonicalKeyEnum.nullable());
/**
 * Import defaults — user tomonidan bir qadamda tanlanishi mumkin bo'lgan
 * global qiymatlar. Excel qatorida bu field bo'lmasa, default ishlatiladi.
 */
export const excelImportDefaultsSchema = z.object({
    stock: z.number().int().min(0).default(0),
    minStock: z.number().int().min(0).default(5),
    priceMultiplier: z.number().min(0).max(1000).default(25),
    currency: z.enum(['UZS', 'USD', 'RUB', 'CNY']).default('UZS'),
    status: z.enum(['MAVJUD', 'KUTILMOQDA', 'TUGAGAN']).default('MAVJUD'),
    categoryId: z.string().uuid().nullable().optional(),
    /**
     * Agar true bo'lsa, Excel'dagi stok qiymatlari e'tiborga olinmaydi va
     * barcha mahsulotlarga defaults.stock qiymati majburiy qo'yiladi.
     * Bulk stock yangilash uchun.
     */
    forceStock: z.boolean().default(false),
    /**
     * Agar true bo'lsa, mahsulot nomlari import vaqtida Latin'dan Kiril'ga
     * o'tkaziladi. Rus/aralash POS tizimidan import qilinayotganda kerak bo'lishi mumkin.
     */
    convertLatinNames: z.boolean().default(false),
    /**
     * Agar true bo'lsa va Excel qatorida SKU yo'q bo'lsa, server avtomatik
     * navbatdagi SKU raqamini (do'kondagi max SKU + 1) tanlab beradi.
     *
     * Faza H+4 dan keyin default `true` — foydalanuvchi SKU haqida o'ylashi
     * shart emas. SKU joyi bo'sh bo'lsa avtomat beriladi (yashirin behavior).
     */
    autoGenerateSku: z.boolean().default(true),
    /**
     * Agar true bo'lsa, server import vaqtida mavjud mahsulotlar bilan
     * nom bo'yicha (case-insensitive) solishtiradi. Duplikat topilsa o'sha
     * qatorni yangi yaratmaydi, `ImportResult.duplicates` ro'yxatiga qo'shadi.
     * SKU orqali upsert'ga ta'sir qilmaydi.
     */
    detectDuplicates: z.boolean().default(true),
    /**
     * Agar true bo'lsa, server qatorlarni nom prefiksi bo'yicha avtomatik
     * guruhlaydi va har guruhdan birinchi qatorni asosiy mahsulot, qolganlarini
     * variant sifatida saqlaydi (eski AvtoFix-main rejimi).
     *
     * Algoritm (eski loyihadagi singari):
     *   1. Birinchi 2 ta so'z bo'yicha guruhlash (`groupByFirstNWords(rows, 2)`)
     *   2. Agar 2-so'zli guruhlash kam guruh bersa (multi-row groups < 30%) —
     *      1-so'z bo'yicha qayta guruhlash (`groupByFirstNWords(rows, 1)`)
     *   3. Har guruhda mavjud mahsulot bor bo'lsa — variantlarga qo'shish
     *   4. Aks holda: birinchi qator = parent product, qolgan = variants
     *
     * Default false (flat rejim) — har qator alohida mahsulot.
     */
    autoGroup: z.boolean().default(false),
    /**
     * Import audit metadata — qaysi fayldan keladi (file.name).
     * Har create qilingan mahsulotning `importSource` ustuniga yoziladi.
     * UI da "Import manbai" panelida ko'rsatiladi (ProductDetailPage).
     */
    importSource: z.string().max(255).optional(),
});
/**
 * Preview request body \u2014 faylni oldindan o'qish uchun.
 *
 * previewLimit: qaytariladigan sample qatorlar soni (default 10, maksimal 500).
 * Wizard'ning review qadamida 500 so'raladi.
 */
export const excelPreviewRequestSchema = z.object({
    previewLimit: z.number().int().min(1).max(500).optional(),
});
/**
 * Tahrirlangan qator \u2014 review qadamida foydalanuvchi o'zgartirgan ma'lumot.
 *
 * rowNumber:
 *   > 0: Excel'dagi mavjud qator raqami (headerRowIndex'dan keyin)
 *   < 0: yangi qo'shilgan qator (user tomonidan wizard'da)
 *
 * data: faqat tahrirlangan canonical key'lar (partial override)
 *
 * deleted: true bo'lsa, import pipeline qatornii o'tkazib yuboradi (skipped)
 */
export const editedRowSchema = z.object({
    rowNumber: z.number().int(),
    data: z.record(excelCanonicalKeyEnum, z.union([z.string(), z.number(), z.null()])),
    deleted: z.boolean().optional(),
});
export const editedRowsSchema = z.array(editedRowSchema);
/**
 * Import request body \u2014 wizard'ning 4-qadamida yuboriladi.
 *
 * columnMapping \u2014 agar user mapping'ni tahrirlagan bo'lsa
 * (aks holda preview'dagi detected ishlatiladi).
 * editedRows \u2014 review qadamida foydalanuvchi tomonidan tahrirlangan
 * qatorlar (optional). Server har qatorda Excel raw ma'lumotiga
 * shu edits'ni merge qiladi.
 */
export const excelImportRequestSchema = z.object({
    columnMapping: columnMappingSchema.optional(),
    defaults: excelImportDefaultsSchema.optional(),
    editedRows: editedRowsSchema.optional(),
});
/**
 * Import natijasi \u2014 server tomonidan qaytariladi.
 *
 * errorXlsxBase64 \u2014 agar xatolar bo'lsa, faqat xato qatorlardan iborat
 * yangi xlsx (base64 encoded). Foydalanuvchi yuklab olib qayta to'g'irlashi
 * va import qilishi mumkin (retry workflow).
 */
export const excelImportResultSchema = z.object({
    totalRows: z.number().int().min(0),
    created: z.number().int().min(0),
    updated: z.number().int().min(0),
    skipped: z.number().int().min(0),
    /**
     * autoGroup rejimida \u2014 yaratilgan/mavjudga qo'shilgan variantlar soni.
     * Flat rejimda 0. UI da result'da "X mahsulot va Y variant qo'shildi" ko'rsatish uchun.
     */
    addedAsVariants: z.number().int().min(0).optional(),
    errors: z.array(z.object({
        row: z.number().int(),
        message: z.string(),
        /** Xato qatordagi original Excel qiymatlari (rawData) */
        rawData: z.record(z.string(), z.unknown()).optional(),
    })),
    duplicates: z
        .array(z.object({
        row: z.number().int(),
        name: z.string(),
        existingProductName: z.string(),
        existingSku: z.string().nullable(),
        /**
         * Fayl ichida bir xil nom takrorlanib, NARXI farq qilsa — saqlangan
         * (1-chi) va o'tkazib yuborilgan (takror) narx. UI buni ochiq
         * ko'rsatadi ("narx 5 ≠ 4.5, 1-chisi saqlandi") — jim tashlanmaydi.
         */
        priceConflict: z
            .object({
            kept: z.number(),
            skipped: z.number(),
        })
            .optional(),
    }))
        .optional(),
    /**
     * Agar xatolar bo'lsa \u2014 faqat xato qatorlardan iborat xlsx (base64).
     * Foydalanuvchi yuklab olib tuzatib qayta import qiladi (retry).
     */
    errorXlsxBase64: z.string().optional(),
});
// ─── Export filtr (Faza H+) ──────────────────────────────────────
/**
 * Mahsulot eksport so'rovi — ixtiyoriy filtrlar:
 *   - categoryId: shu kategoriya (va bevosita sub-kategoriyalari) mahsulotlari
 *   - importSource: aynan shu Excel fayldan import qilingan mahsulotlar
 *
 * Ikkalasi ham berilmasa — barcha mahsulotlar (eski xulq).
 * GET query bo'lgani uchun string'lar (categoryId UUID, importSource erkin matn).
 */
export const excelExportQuerySchema = z.object({
    categoryId: uuidSchema.optional(),
    importSource: z.string().trim().min(1).max(255).optional(),
    /**
     * Narxli/narxsiz eksport — agar `false` bo'lsa, xlsx faylda Tannarx,
     * Markup % va Sotuv narxi ustunlari OLIB TASHLANADI (taminotchiga yoki
     * mijozlarga maxfiy narxlarsiz mahsulot ro'yxatini yuborish uchun).
     *
     * GET query string'dan keladi — z.coerce.boolean() ishlatib bo'lmaydi
     * (Boolean("false") = true JavaScript'da). Shu sababli manual parsing:
     * "false"/"0" → false, qolgani → true.
     */
    withPrices: z
        .union([z.boolean(), z.string()])
        .transform((v) => {
        if (typeof v === 'boolean')
            return v;
        const lower = v.toLowerCase().trim();
        return lower !== 'false' && lower !== '0' && lower !== '';
    })
        .default(true)
        .optional(),
    /**
     * Faqat RASMSIZ mahsulotlar — `true` bo'lsa, faqat rasmi yo'q mahsulotlar
     * (image yo'q VA images bo'sh) eksport qilinadi. Rasmi yetishmayotganlarni
     * topish uchun. Query string'dan: "true"/"1" → true, qolgani → false.
     */
    onlyWithoutImage: z
        .union([z.boolean(), z.string()])
        .transform((v) => {
        if (typeof v === 'boolean')
            return v;
        const lower = v.toLowerCase().trim();
        return lower === 'true' || lower === '1';
    })
        .default(false)
        .optional(),
    /**
     * Faqat STOKI TUGAGAN (0 yoki manfiy) mahsulotlar — `true` bo'lsa, faqat
     * stock <= 0 bo'lgan qatorlar (parent YOKI xil alohida) eksport qilinadi.
     * Nimani qayta buyurtma qilish kerakligini bilish uchun. Query string'dan:
     * "true"/"1" → true, qolgani → false.
     */
    onlyZeroStock: z
        .union([z.boolean(), z.string()])
        .transform((v) => {
        if (typeof v === 'boolean')
            return v;
        const lower = v.toLowerCase().trim();
        return lower === 'true' || lower === '1';
    })
        .default(false)
        .optional(),
});
//# sourceMappingURL=excel.schema.js.map